import "@/App.css";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  CalendarCheck,
  Car,
  CheckCircle2,
  Clock3,
  Droplets,
  ExternalLink,
  MapPin,
  MoonStar,
  Navigation,
  ShieldCheck,
  Sparkles,
  Star,
  Instagram,
  Users,
  Zap,
} from "lucide-react";
import { Icon } from "@iconify/react";

const HUDLE_LINKS = {
  ardee: "https://hudle.in/venues/active-box-ardee-city/191179",
  sector57: "https://hudle.in/venues/active-box-sector-57/941413",
};

const BRAND_LOGO = "/activebox-logo-v2-clean.png";

const MAP_LINKS = {
  ardee: "https://maps.app.goo.gl/wAM2HKqJZ9rPUnQu5",
  sector57: "https://maps.app.goo.gl/vQbQy9nJtNGuvLkX9",
};

const INSTAGRAM_LINK =
  "https://www.instagram.com/activebox.in?igsh=MXIyODAweG9veXZycA==";

const NAV_ITEMS = [
  { id: "about", label: "About", testId: "nav-about-link" },
  { id: "locations", label: "Locations", testId: "nav-locations-link" },
  { id: "gallery", label: "Gallery", testId: "nav-gallery-link" },
  { id: "maps", label: "Map", testId: "nav-map-link" },
];

const GALLERY_FILTERS = [
  { value: "all", label: "All Photos" },
  { value: "sector57", label: "Sector 57" },
  { value: "ardee", label: "Ardee City" },
];

const locations = [
  {
    id: "ardee-city",
    name: "Ardee City – Gurgaon",
    sports: ["Box Cricket"],
    timings: ["7:00 AM – 8:30 PM", "Open all 7 days"],
    facilities: ["Floodlights", "Parking", "Drinking Water", "Quality Turf"],
    hudleUrl: HUDLE_LINKS.ardee,
    mapUrl: MAP_LINKS.ardee,
  },
  {
    id: "sector-57",
    name: "Sector 57 – Gurgaon",
    sports: ["Box Cricket", "Box Football", "Pickleball"],
    timings: ["6:00 AM – 11:30 PM", "Open all 7 days"],
    facilities: ["Floodlights", "Parking", "Drinking Water", "Quality Turf"],
    hudleUrl: HUDLE_LINKS.sector57,
    mapUrl: MAP_LINKS.sector57,
  },
];

const sports = [
  {
    title: "Box Cricket",
    description:
      "Fast-paced cricket in a compact turf format perfect for small groups.",
    icon: "mdi:cricket",
  },
  {
    title: "Box Football",
    description:
      "Quick and exciting football games in a smaller turf arena.",
    icon: "mdi:soccer",
  },
  {
    title: "Pickleball",
    description:
      "A fast-growing sport combining elements of tennis, badminton and table tennis.",
    icon: "fa6-solid:table-tennis-paddle-ball",
  },
];

const whyActiveBoxFeatures = [
  {
    title: "Premium Turf Surface",
    icon: Sparkles,
  },
  {
    title: "Bright Floodlights",
    icon: Zap,
  },
  {
    title: "Easy Bookings via Hudle",
    icon: CalendarCheck,
  },
  {
    title: "Prime Gurgaon Locations",
    icon: MapPin,
  },
  {
    title: "Perfect for Group Games",
    icon: Users,
  },
];

const seoContentBlocks = [
  {
    title: "Box Cricket in Gurgaon",
    text: "Enjoy match-ready turf lanes and smooth Hudle booking for energetic box cricket sessions.",
  },
  {
    title: "Box Football in Gurgaon",
    text: "Play fast-paced turf football under bright lights at our Sector 57 Active Box facility.",
  },
  {
    title: "Pickleball in Gurgaon",
    text: "Experience premium pickleball courts with clean play zones, evening lighting and easy slot booking.",
  },
];

const facilities = [
  { label: "Flood Lights", icon: Zap },
  { label: "Parking", icon: Car },
  { label: "Drinking Water", icon: Droplets },
  { label: "Premium Turf Surface", icon: ShieldCheck },
  { label: "Night Play", icon: MoonStar },
  { label: "Easy Booking via Hudle", icon: CalendarCheck },
];

const galleryImages = [
  {
    id: "sector57-turf-wide-night",
    title: "Sector 57 – Floodlit Box Football Turf",
    location: "sector57",
    image:
      "https://customer-assets.emergentagent.com/job_active-box-turf/artifacts/ei7nl37k_WhatsApp%20Image%202026-02-05%20at%2013.25.53%20%281%29.jpeg",
  },
  {
    id: "sector57-cricket-strip-night",
    title: "Sector 57 – Premium Box Cricket Strip",
    location: "sector57",
    image:
      "https://customer-assets.emergentagent.com/job_active-box-turf/artifacts/75w1qb99_WhatsApp%20Image%202026-02-05%20at%2013.25.55%20%283%29.jpeg",
  },
  {
    id: "sector57-pickleball-day",
    title: "Sector 57 – Professional Pickleball Courts",
    location: "sector57",
    image:
      "https://customer-assets.emergentagent.com/job_active-box-turf/artifacts/hdrdha7w_WhatsApp%20Image%202026-02-05%20at%2013.25.56%20%281%29.jpeg",
  },
  {
    id: "sector57-pickleball-night",
    title: "Sector 57 – Evening Pickleball Sessions",
    location: "sector57",
    image:
      "https://customer-assets.emergentagent.com/job_active-box-turf/artifacts/1pcog8wh_WhatsApp%20Image%202026-02-05%20at%2013.25.57%20%284%29.jpeg",
  },
  {
    id: "sector57-turf-angle-night",
    title: "Sector 57 – Match-Ready Turf Arena",
    location: "sector57",
    image:
      "https://customer-assets.emergentagent.com/job_active-box-turf/artifacts/i1jfs89a_WhatsApp%20Image%202026-02-05%20at%2013.25.54%20%281%29.jpeg",
  },
  {
    id: "ardee-turf-night-wide",
    title: "Ardee City – Match-Ready Cricket Turf",
    location: "ardee",
    image:
      "https://customer-assets.emergentagent.com/job_active-box-turf/artifacts/r5nh1pa2_WhatsApp%20Image%202026-02-05%20at%2012.48.28.jpeg",
  },
  {
    id: "ardee-cricket-strip-day",
    title: "Ardee City – Premium Cricket Strip",
    location: "ardee",
    image:
      "https://customer-assets.emergentagent.com/job_active-box-turf/artifacts/vjaqmtpp_WhatsApp%20Image%202026-02-05%20at%2012.48.30%20%282%29.jpeg",
  },
];

const SectionHeading = ({ eyebrow, title, subtitle, testIdPrefix }) => (
  <div className="mb-8 space-y-3 reveal">
    <p
      className="text-xs uppercase tracking-[0.28em] text-emerald-300"
      data-testid={`${testIdPrefix}-eyebrow`}
    >
      {eyebrow}
    </p>
    <h2
      className="text-3xl font-semibold text-white sm:text-4xl"
      data-testid={`${testIdPrefix}-title`}
    >
      {title}
    </h2>
    <p
      className="max-w-3xl text-sm text-slate-300 sm:text-base"
      data-testid={`${testIdPrefix}-subtitle`}
    >
      {subtitle}
    </p>
  </div>
);

function App() {
  const [activeSection, setActiveSection] = useState("about");
  const [galleryFilter, setGalleryFilter] = useState("all");

  useEffect(() => {
    const sectionIds = NAV_ITEMS.map((item) => item.id);

    const updateActiveSection = () => {
      const scanLine = window.scrollY + 180;
      let current = sectionIds[0];

      sectionIds.forEach((id) => {
        const section = document.getElementById(id);
        if (section && section.offsetTop <= scanLine) {
          current = id;
        }
      });

      setActiveSection(current);
    };

    updateActiveSection();
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    return () => window.removeEventListener("scroll", updateActiveSection);
  }, []);

  const filteredGalleryImages =
    galleryFilter === "all"
      ? galleryImages
      : galleryImages.filter((image) => image.location === galleryFilter);

  return (
    <div className="active-box-page" data-testid="active-box-page">
      <header
        className="sticky top-0 z-50 border-b border-emerald-900/40 bg-slate-950/85 backdrop-blur-lg"
        data-testid="main-header"
      >
        <div className="section-shell flex items-center justify-between py-4">
          <a
            href="#hero"
            className="brand-link"
            data-testid="header-brand-link"
          >
            <img
              src={BRAND_LOGO}
              alt="Active Box logo"
              className="brand-logo"
              loading="eager"
              data-testid="header-brand-logo"
            />
            <span className="sr-only" data-testid="header-brand-text">Active Box</span>
          </a>
          <nav className="hidden items-center gap-6 md:flex" data-testid="header-nav">
            {NAV_ITEMS.map((item) => (
              <a
                href={`#${item.id}`}
                key={item.id}
                className={`nav-link ${activeSection === item.id ? "nav-link-active" : ""}`}
                data-testid={item.testId}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <a
            href={INSTAGRAM_LINK}
            target="_blank"
            rel="noreferrer"
            className="header-insta-link"
            data-testid="header-instagram-link"
          >
            <Instagram className="h-4 w-4" /> @activebox.in
          </a>
          <Button asChild className="book-btn" data-testid="header-book-now-button">
            <a href={HUDLE_LINKS.sector57} target="_blank" rel="noreferrer">
              Book Now
            </a>
          </Button>
        </div>
      </header>

      <main data-testid="main-content">
        <section id="hero" className="hero-section" data-testid="hero-section">
          <div className="hero-image-shell section-shell reveal">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/b41f993b-1800-4d01-828f-afefbf068e8b/images/5d7c0924cce44ada3cc6e79e5757e1ffe644946dbcdbc0edca6cede253ee117d.png"
              alt="Active Box turf in Gurgaon"
              className="hero-image"
              loading="eager"
              data-testid="hero-background-image"
            />
            <div className="hero-overlay" data-testid="hero-overlay" />
            <div className="hero-content" data-testid="hero-content">
              <div className="hero-logo-badge" data-testid="hero-logo-badge">
                <img
                  src={BRAND_LOGO}
                  alt="Active Box official logo"
                  className="hero-logo-image"
                  loading="eager"
                  data-testid="hero-brand-logo"
                />
              </div>
              <p
                className="text-xs uppercase tracking-[0.34em] text-emerald-200"
                data-testid="hero-location-tag"
              >
                Gurgaon, India
              </p>
              <h1 className="text-2xl font-semibold text-white sm:text-4xl lg:text-5xl" data-testid="hero-title">
                Box Cricket, Box Football & Pickleball Turf in Gurgaon
              </h1>
              <p className="text-sm text-emerald-100 sm:text-lg" data-testid="hero-subtitle">
                Play at Active Box in Ardee City and Sector 57, Gurgaon — with premium turf,
                bright floodlights, parking, drinking water, and easy booking on Hudle.
              </p>
              <div className="mt-6 flex flex-wrap gap-3" data-testid="hero-cta-group">
                <Button asChild className="book-btn text-sm" data-testid="hero-book-now-button">
                  <a href={HUDLE_LINKS.sector57} target="_blank" rel="noreferrer">
                    Book Now on Hudle <ExternalLink className="ml-1 h-4 w-4" />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-emerald-200/60 bg-slate-900/20 text-emerald-100 hover:bg-emerald-600/20"
                  data-testid="hero-view-locations-button"
                >
                  <a href="#locations">View Locations</a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="instagram-icon-link"
                  data-testid="hero-instagram-icon-button"
                >
                  <a
                    href={INSTAGRAM_LINK}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open Active Box Instagram"
                    title="Open Active Box Instagram"
                  >
                    <Instagram className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="section-shell pb-6" data-testid="social-proof-section">
          <div className="glass-card reveal" data-testid="social-proof-card">
            <div className="flex items-center gap-1" data-testid="social-proof-stars">
              {Array.from({ length: 5 }).map((_, index) => (
                <Star
                  key={`star-${index}`}
                  className="h-4 w-4 fill-emerald-300 text-emerald-300"
                  data-testid={`social-proof-star-${index + 1}`}
                />
              ))}
            </div>
            <p className="text-sm text-slate-100 sm:text-base" data-testid="social-proof-text">
              Trusted by Gurgaon players on Hudle for quality turf and smooth booking.
            </p>
            <a
              href={HUDLE_LINKS.ardee}
              className="inline-flex items-center gap-2 text-sm font-medium text-emerald-300 transition-colors hover:text-emerald-100"
              target="_blank"
              rel="noreferrer"
              data-testid="social-proof-link"
            >
              View Hudle Reviews <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </section>

        <section id="about" className="section-shell py-12 sm:py-14" data-testid="about-section">
          <SectionHeading
            eyebrow="About Active Box"
            title="Premium turf built for serious, high-energy play"
            subtitle="Active Box is a premium sports turf destination in Gurgaon for fast-paced box cricket, football and pickleball. Whether you’re playing with friends, training, or hosting group games, we keep the experience smooth from turf quality to lighting and booking."
            testIdPrefix="about-section"
          />
        </section>

        <section id="locations" className="section-shell py-7 sm:py-9" data-testid="locations-section">
          <SectionHeading
            eyebrow="Locations"
            title="Choose your nearest Active Box arena"
            subtitle="Both locations are open all 7 days with quality turf, bright floodlights, and quick booking via Hudle."
            testIdPrefix="locations-section"
          />
          <div className="grid gap-6 lg:grid-cols-2" data-testid="locations-card-grid">
            {locations.map((location) => (
              <article className="glass-card reveal" key={location.id} data-testid={`location-card-${location.id}`}>
                <h3
                  className="inline-flex items-center gap-2 text-2xl font-semibold text-white"
                  data-testid={`location-name-${location.id}`}
                >
                  <MapPin className="h-5 w-5 text-emerald-300" data-testid={`location-icon-${location.id}`} />
                  {location.name}
                </h3>

                <div className="space-y-3" data-testid={`location-sports-wrapper-${location.id}`}>
                  <p className="text-xs uppercase tracking-[0.22em] text-emerald-300" data-testid={`location-sports-label-${location.id}`}>
                    Sports Offered
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid={`location-sports-list-${location.id}`}>
                    {location.sports.map((sport) => (
                      <span className="pill" key={sport} data-testid={`location-sport-${location.id}-${sport.toLowerCase().replace(/\s+/g, "-")}`}>
                        {sport}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid gap-2 text-sm text-slate-200" data-testid={`location-timings-${location.id}`}>
                  <p className="inline-flex items-center gap-2" data-testid={`location-time-slot-${location.id}`}>
                    <Clock3 className="h-4 w-4 text-emerald-300" />
                    {location.timings[0]}
                  </p>
                  <p className="inline-flex items-center gap-2" data-testid={`location-open-days-${location.id}`}>
                    <CalendarCheck className="h-4 w-4 text-emerald-300" />
                    {location.timings[1]}
                  </p>
                </div>

                <div data-testid={`location-facilities-${location.id}`}>
                  <p className="mb-3 text-xs uppercase tracking-[0.22em] text-emerald-300" data-testid={`location-facilities-label-${location.id}`}>
                    Facilities
                  </p>
                  <ul className="grid grid-cols-2 gap-2 text-sm text-slate-200" data-testid={`location-facility-list-${location.id}`}>
                    {location.facilities.map((item) => (
                      <li className="inline-flex items-center gap-2" key={item} data-testid={`location-facility-item-${location.id}-${item.toLowerCase().replace(/\s+/g, "-")}`}>
                        <ShieldCheck className="h-4 w-4 text-emerald-300" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-wrap gap-3" data-testid={`location-actions-${location.id}`}>
                  <Button asChild className="book-btn" data-testid={`location-book-button-${location.id}`}>
                    <a href={location.hudleUrl} target="_blank" rel="noreferrer">
                      Book on Hudle
                    </a>
                  </Button>
                  <Button asChild variant="secondary" className="bg-slate-800 text-slate-100 hover:bg-slate-700" data-testid={`location-map-button-${location.id}`}>
                    <a href={location.mapUrl} target="_blank" rel="noreferrer">
                      Open Map <Navigation className="ml-1 h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="section-shell py-7 sm:py-9" data-testid="why-active-box-section">
          <SectionHeading
            eyebrow="Why Active Box"
            title="Why Active Box"
            subtitle="Built for players who want premium quality with a friendly, hassle-free experience every time."
            testIdPrefix="why-active-box"
          />

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="why-active-box-grid">
            {whyActiveBoxFeatures.map((feature) => {
              const FeatureIcon = feature.icon;
              return (
                <article
                  key={feature.title}
                  className="why-feature-card reveal"
                  data-testid={`why-active-box-card-${feature.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div
                    className="why-feature-icon-wrap"
                    data-testid={`why-active-box-icon-wrap-${feature.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <FeatureIcon
                      className="h-4 w-4 text-emerald-200"
                      data-testid={`why-active-box-icon-${feature.title.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  </div>
                  <p
                    className="text-sm font-medium text-slate-100"
                    data-testid={`why-active-box-text-${feature.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {feature.title}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        <section id="sports" className="section-shell py-12" data-testid="sports-section">
          <SectionHeading
            eyebrow="Sports"
            title="Play your game, your pace"
            subtitle="Compact formats, quick matches and a high-energy vibe for every skill level."
            testIdPrefix="sports-section"
          />
          <div className="grid gap-5 md:grid-cols-3" data-testid="sports-card-grid">
            {sports.map((sport) => {
              return (
                <article className="glass-card reveal" key={sport.title} data-testid={`sports-card-${sport.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div
                    className="sport-icon-badge"
                    data-testid={`sports-icon-badge-${sport.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon
                      icon={sport.icon}
                      width="34"
                      height="34"
                      aria-hidden="true"
                      className="sport-icon"
                      data-testid={`sports-icon-${sport.title.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-white" data-testid={`sports-title-${sport.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    {sport.title}
                  </h3>
                  <p className="text-sm text-slate-300" data-testid={`sports-description-${sport.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    {sport.description}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        <section id="facilities" className="section-shell py-7 sm:py-9" data-testid="facilities-section">
          <SectionHeading
            eyebrow="Facilities"
            title="Everything you need for a great game"
            subtitle="Premium on-ground amenities designed for comfort, safety and smooth sessions."
            testIdPrefix="facilities-section"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="facilities-grid">
            {facilities.map((facility) => {
              const FacilityIcon = facility.icon;
              return (
                <div className="facility-pill reveal" key={facility.label} data-testid={`facility-card-${facility.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <FacilityIcon className="h-5 w-5 text-emerald-300" data-testid={`facility-icon-${facility.label.toLowerCase().replace(/\s+/g, "-")}`} />
                  <span className="text-sm text-slate-100" data-testid={`facility-label-${facility.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {facility.label}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        <section id="gallery" className="section-shell py-12" data-testid="gallery-section">
          <SectionHeading
            eyebrow="Gallery"
            title="The Active Box experience"
            subtitle="From day sessions to floodlit nights — see the turf, the action and the energy at both locations."
            testIdPrefix="gallery-section"
          />

          <div className="gallery-filter-wrap" data-testid="gallery-filter-wrap">
            {GALLERY_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                className={`gallery-filter-tab ${galleryFilter === filter.value ? "gallery-filter-tab-active" : ""}`}
                onClick={() => setGalleryFilter(filter.value)}
                data-testid={`gallery-filter-${filter.value}`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
            {filteredGalleryImages.map((item, index) => (
              <figure
                className={`gallery-card reveal ${index === 0 ? "sm:col-span-2" : ""}`}
                key={item.id}
                data-testid={`gallery-card-${item.id}`}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="gallery-image"
                  loading="lazy"
                  data-testid={`gallery-image-${item.id}`}
                />
                <figcaption className="gallery-caption" data-testid={`gallery-caption-${item.id}`}>
                  {item.title}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="maps" className="section-shell py-9 sm:py-11" data-testid="maps-section">
          <SectionHeading
            eyebrow="Map"
            title="Find Active Box instantly"
            subtitle="Use these maps to quickly navigate to either Active Box location in Gurgaon."
            testIdPrefix="maps-section"
          />
          <div className="grid gap-6 lg:grid-cols-2" data-testid="maps-grid">
            <article className="glass-card reveal" data-testid="map-card-ardee-city">
              <h3 className="text-xl font-semibold text-white" data-testid="map-title-ardee-city">
                Active Box – Ardee City, Gurgaon
              </h3>
              <div className="map-shell" data-testid="map-iframe-shell-ardee-city">
                <iframe
                  title="Active Box Ardee City Gurgaon"
                  src="https://www.google.com/maps?q=28.4418114,77.0769299&z=16&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  data-testid="map-iframe-ardee-city"
                />
              </div>
              <a
                href={MAP_LINKS.ardee}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-emerald-300 transition-colors hover:text-emerald-100"
                data-testid="map-open-link-ardee-city"
              >
                Open in Google Maps <ExternalLink className="h-4 w-4" />
              </a>
            </article>

            <article className="glass-card reveal" data-testid="map-card-sector-57">
              <h3 className="text-xl font-semibold text-white" data-testid="map-title-sector-57">
                Active Box – Sector 57, Gurgaon
              </h3>
              <div className="map-shell" data-testid="map-iframe-shell-sector-57">
                <iframe
                  title="Active Box Sector 57 Gurgaon"
                  src="https://www.google.com/maps?q=28.4244294,77.0872853&z=16&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  data-testid="map-iframe-sector-57"
                />
              </div>
              <a
                href={MAP_LINKS.sector57}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-emerald-300 transition-colors hover:text-emerald-100"
                data-testid="map-open-link-sector-57"
              >
                Open in Google Maps <ExternalLink className="h-4 w-4" />
              </a>
            </article>
          </div>
        </section>

        <section className="section-shell py-9 sm:py-11" data-testid="local-seo-section">
          <SectionHeading
            eyebrow="Local Search"
            title="Looking for a turf in Gurgaon?"
            subtitle="Active Box offers premium box cricket, box football and pickleball facilities in Gurgaon. Our Ardee City location offers box cricket, while Sector 57 offers box cricket, box football and pickleball. Both locations are open all 7 days and can be booked easily via Hudle."
            testIdPrefix="local-seo"
          />

          <div className="grid gap-4 md:grid-cols-3" data-testid="local-seo-grid">
            {seoContentBlocks.map((block) => (
              <article
                key={block.title}
                className="seo-mini-card reveal"
                data-testid={`local-seo-card-${block.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <h3
                  className="inline-flex items-center gap-2 text-lg font-semibold text-white"
                  data-testid={`local-seo-title-${block.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                  {block.title}
                </h3>
                <p
                  className="text-sm text-slate-300"
                  data-testid={`local-seo-text-${block.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {block.text}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section className="section-shell py-14" data-testid="final-cta-section">
          <div className="cta-shell reveal" data-testid="final-cta-card">
            <p className="text-xs uppercase tracking-[0.3em] text-emerald-300" data-testid="final-cta-eyebrow">
              Ready to Play?
            </p>
            <h2 className="text-3xl font-semibold text-white sm:text-4xl" data-testid="final-cta-title">
              Ready to Play?
            </h2>
            <p className="max-w-2xl text-sm text-slate-300 sm:text-base" data-testid="final-cta-text">
              Check live availability and pricing on Hudle, then book your preferred slot in seconds.
            </p>
            <Button asChild className="book-btn" data-testid="final-cta-book-now-button">
              <a href={HUDLE_LINKS.sector57} target="_blank" rel="noreferrer">
                Book Now on Hudle <ExternalLink className="ml-1 h-4 w-4" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <footer className="section-shell pb-10" data-testid="site-footer">
        <div className="flex flex-col items-start justify-between gap-3 border-t border-emerald-900/40 pt-6 text-xs text-slate-400 sm:flex-row sm:items-center">
          <div className="footer-brand-wrap" data-testid="footer-brand-wrap">
            <img
              src={BRAND_LOGO}
              alt="Active Box footer logo"
              className="footer-brand-logo"
              loading="lazy"
              data-testid="footer-brand-logo"
            />
            <div>
              <p data-testid="footer-brand-text">© {new Date().getFullYear()} Active Box • Gurgaon, India</p>
              <p className="text-[11px] text-slate-500" data-testid="footer-seo-line">
                Active Box | Ardee City & Sector 57, Gurgaon | Box Cricket, Box Football & Pickleball
              </p>
            </div>
          </div>
          <div className="footer-links-wrap" data-testid="footer-links-wrap">
            <a
              href={INSTAGRAM_LINK}
              target="_blank"
              rel="noreferrer"
              className="instagram-icon-link footer-instagram-icon"
              data-testid="footer-instagram-link"
              aria-label="Open Active Box Instagram"
              title="Open Active Box Instagram"
            >
              <Instagram className="h-3.5 w-3.5" />
            </a>
            <a
              href={HUDLE_LINKS.ardee}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-100"
              data-testid="footer-booking-link"
            >
              Bookings via Hudle <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
